/**
 * MatchController: the only thing in the UI that talks to the engine reducer.
 *
 * Why a controller and not a store: a match is a state machine with a driver
 * loop in it. The bot has to take several actions in a row with a pause between
 * each so a human can watch what happened, and that loop has to survive React
 * re-renders, be cancellable on unmount, and be testable without a DOM. A plain
 * class with a subscribe callback does all three; components read it through
 * `useMatch`.
 *
 * Responsibilities:
 *   - owns GameState and applies every action through `reduce`
 *   - records (seed, decks, actions[]) so any match can be replayed exactly
 *   - buffers the events from each reduce so the battlefield can animate them
 *   - drives the bot one action at a time with a delay, respecting reduce motion
 *   - catches RulesError and surfaces `reason`, which is the sentence the rules
 *     doc says the player should read
 *
 * Everything it exposes is derived from the engine. It never decides a rule.
 */

import {
  RulesError,
  createGame,
  isGameOver,
  legalActions,
  nameMapFor,
  redactedView,
  reduce,
  renderEvent,
  type Action,
  type GameEvent,
  type GameState,
} from '@/engine';
import type { Bot, BotDecision, BotDifficulty, CreateBotOptions } from '@/ai/types';
import type { MatchSummary } from '@/app/stores/matchHistory';
import { buildSummary, createTelemetry, observe, type MatchTelemetry } from './matchStats';
import { createStubBot } from './stubBot';

export const LOCAL_PLAYER = 'p1';
export const BOT_PLAYER = 'p2';

export type MatchMode = 'bot' | 'deckTest' | 'lab' | 'tutorial';

export interface MatchConfig {
  seed: number;
  mode: MatchMode;
  playerName: string;
  opponentName: string;
  playerDeckId: string;
  opponentDeckId: string;
  playerDeck: string[];
  opponentDeck: string[];
  difficulty: BotDifficulty;
  /** When false the local player is always on the play. */
  randomFirstPlayer: boolean;
  /**
   * Skip the opening redraw. The tutorial uses this: its first real
   * instruction is "install a Wallet", and a mulligan prompt in front of that
   * is a rule the script has not taught yet.
   */
  skipRedraw?: boolean;
  /** Milliseconds between bot actions. 0 under reduce motion. */
  botDelayMs: number;
}

export interface LogLine {
  id: number;
  turn: number;
  text: string;
  /** Instance ids mentioned, so the log can link a name to the zoom panel. */
  refs: string[];
}

export interface MatchSnapshot {
  state: GameState;
  log: LogLine[];
  /** Events from the most recent reduce, for animation. */
  lastEvents: GameEvent[];
  botThinking: boolean;
  /** Set once the game is over, so the result is recorded exactly once. */
  finished: boolean;
  /** The bot's most recent stated reason, for DECK TEST and the Test Lab. */
  lastBotReason: string | null;
}

/** A complete, replayable description of a match. RULES section 12. */
export interface ReplayDescriptor {
  version: number;
  seed: number;
  players: { id: string; name: string; deck: string[] }[];
  actions: Action[];
}

type Listener = (snapshot: MatchSnapshot) => void;

interface AiModule {
  createBot?: (options: CreateBotOptions) => Bot;
}

/**
 * `import.meta.glob` is used rather than a bare dynamic import because
 * src/ai/index.ts may not exist yet: glob returns an empty record instead of
 * failing the build, which is exactly the fallback behaviour we want.
 */
const aiModules = import.meta.glob('/src/ai/index.ts') as Record<string, () => Promise<unknown>>;

async function loadBot(options: CreateBotOptions): Promise<Bot> {
  const loader = aiModules['/src/ai/index.ts'];
  if (loader) {
    try {
      const mod = (await loader()) as AiModule;
      if (typeof mod.createBot === 'function') return mod.createBot(options);
    } catch (error) {
      console.warn('[gaswars] src/ai failed to load, falling back to the stub bot.', error);
    }
  }
  return createStubBot(options);
}

/** Hard cap on consecutive bot actions, so a driver bug cannot hang the tab. */
const MAX_BOT_STEPS = 600;

export class MatchController {
  readonly config: MatchConfig;

  private state: GameState;
  private readonly actions: Action[] = [];
  private log: LogLine[] = [];
  private lastEvents: GameEvent[] = [];
  private logId = 0;
  private botThinking = false;
  private finished = false;
  private lastBotReason: string | null = null;
  private bot: Bot | null = null;
  private telemetry: MatchTelemetry;
  private disposed = false;
  private pumping = false;
  private readonly listeners = new Set<Listener>();
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();

  constructor(config: MatchConfig) {
    this.config = config;
    const startingPlayer = config.randomFirstPlayer ? undefined : LOCAL_PLAYER;
    const created = createGame({
      seed: config.seed,
      players: [
        { id: LOCAL_PLAYER, name: config.playerName, deck: config.playerDeck },
        { id: BOT_PLAYER, name: config.opponentName, deck: config.opponentDeck },
      ],
      options: {
        ...(startingPlayer ? { startingPlayer } : {}),
        ...(config.skipRedraw ? { skipRedraw: true } : {}),
      },
    });
    this.state = created.state;
    this.telemetry = createTelemetry(created.state, LOCAL_PLAYER);
    this.appendLog(created.events);
    this.lastEvents = created.events;
  }

  // --- lifecycle ---------------------------------------------------------

  /**
   * Load the bot and let it take any opening actions it owns.
   *
   * `disposed` is cleared here on purpose: React StrictMode mounts, unmounts
   * and remounts an effect in development, so a controller that could not come
   * back from a dispose would leave every dev match frozen on turn one.
   */
  async start(): Promise<void> {
    this.disposed = false;
    this.bot = await loadBot({
      difficulty: this.config.difficulty,
      archetype: undefined,
      seed: this.config.seed ^ 0x5f5f,
    });
    if (this.disposed) return;
    this.emit();
    await this.pumpBot();
  }

  dispose(): void {
    this.disposed = true;
    for (const timer of this.timers) clearTimeout(timer);
    this.timers.clear();
    this.listeners.clear();
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    listener(this.snapshot());
    return () => this.listeners.delete(listener);
  }

  snapshot(): MatchSnapshot {
    return {
      state: this.state,
      log: this.log,
      lastEvents: this.lastEvents,
      botThinking: this.botThinking,
      finished: this.finished,
      lastBotReason: this.lastBotReason,
    };
  }

  // --- reads the battlefield needs ---------------------------------------

  getState(): GameState {
    return this.state;
  }

  /** Every action the engine would currently accept from the local player. */
  localLegalActions(): Action[] {
    if (isGameOver(this.state)) return [];
    return legalActions(this.state, LOCAL_PLAYER);
  }

  isLocalTurn(): boolean {
    return this.state.activePlayer === LOCAL_PLAYER;
  }

  /** (seed, decks, actions) is the whole match. RULES section 12. */
  replayDescriptor(): ReplayDescriptor {
    return {
      version: this.state.version,
      seed: this.state.seed,
      players: [
        { id: LOCAL_PLAYER, name: this.config.playerName, deck: this.config.playerDeck },
        { id: BOT_PLAYER, name: this.config.opponentName, deck: this.config.opponentDeck },
      ],
      actions: this.actions.slice(),
    };
  }

  replayJson(): string {
    return JSON.stringify(this.replayDescriptor(), null, 2);
  }

  // --- the one write path ------------------------------------------------

  /**
   * Apply a local player action. Returns the RulesError reason when the engine
   * rejects it, so the caller can toast the sentence the player should see.
   * Never throws: an illegal click is a normal thing for a UI to produce.
   */
  dispatch(action: Action): { ok: true } | { ok: false; reason: string; code: string } {
    if (this.disposed) return { ok: false, reason: 'This match has ended.', code: 'INVALID_ACTION' };
    try {
      this.apply(action);
    } catch (error) {
      if (error instanceof RulesError) return { ok: false, reason: error.reason, code: error.code };
      throw error;
    }
    this.emit();
    void this.pumpBot();
    return { ok: true };
  }

  private apply(action: Action): void {
    const before = this.state;
    const result = reduce(before, action);
    this.state = result.state;
    this.actions.push(action);
    this.lastEvents = result.events;
    // Telemetry is folded in here rather than read off the final state,
    // because most of what the achievements ask about is a fact of the whole
    // match and not of its last frame. See matchStats.ts.
    observe(this.telemetry, before, result.state, result.events);
    this.appendLog(result.events);
    if (isGameOver(this.state)) this.finished = true;
  }

  /**
   * The full match summary, measured rather than guessed. Safe to call more
   * than once: it reads the accumulator and never mutates it.
   */
  summary(): Omit<MatchSummary, 'id' | 'playedAt'> {
    return buildSummary({
      telemetry: this.telemetry,
      state: this.state,
      mode: this.config.mode,
      playerDeckId: this.config.playerDeckId,
      opponentDeckId: this.config.opponentDeckId,
      opponentName: this.config.opponentName,
      difficulty: this.config.difficulty,
      seed: this.config.seed,
      playerDeck: this.config.playerDeck,
    });
  }

  // --- bot driver --------------------------------------------------------

  /**
   * Let the bot take every action it currently owns, one at a time, pausing
   * between each so the player can follow what happened. Re-entrant calls are
   * ignored: one pump loop runs at a time.
   */
  private async pumpBot(): Promise<void> {
    if (this.pumping || this.disposed || !this.bot) return;
    this.pumping = true;
    try {
      let steps = 0;
      while (!this.disposed && !isGameOver(this.state) && steps < MAX_BOT_STEPS) {
        // `legalActions` returns `Concede` even for a player who holds no
        // priority at all, so a non-empty list is not by itself a cue to act.
        // Filtering it out is also the right policy on its own terms: a bot
        // should never resign a match it was asked to play.
        const legal = legalActions(this.state, BOT_PLAYER).filter((a) => a.type !== 'Concede');
        if (legal.length === 0) break;

        this.setBotThinking(true);
        await this.wait(this.config.botDelayMs);
        if (this.disposed) return;

        const decision = this.askBot(legal);
        if (!decision) break;
        this.lastBotReason = decision.reason ?? null;

        try {
          this.apply(decision.action);
        } catch (error) {
          if (!(error instanceof RulesError)) throw error;
          // A bot that proposes an illegal action would otherwise spin here.
          console.warn('[gaswars] bot proposed an illegal action.', error.code, error.reason, decision.action);
          break;
        }
        steps++;
        this.emit();
      }
      if (steps >= MAX_BOT_STEPS) {
        console.warn('[gaswars] bot driver hit its step cap and stopped.');
      }
    } finally {
      this.setBotThinking(false);
      this.pumping = false;
      this.emit();
    }
  }

  private askBot(legal: Action[]): BotDecision | null {
    if (!this.bot) return null;
    try {
      return this.bot.chooseAction({
        playerId: BOT_PLAYER,
        view: redactedView(this.state, BOT_PLAYER),
        legal,
        random: this.botRandom,
        archetype: undefined,
      });
    } catch (error) {
      console.warn('[gaswars] bot threw while choosing an action.', error);
      return { action: legal[0] };
    }
  }

  /**
   * The bot's own RNG, seeded from the match seed and stepped per call. It is
   * deliberately separate from the game RNG: the bot must never be able to
   * influence a shuffle, and a replay must reproduce both.
   */
  private botSeed = 0;
  private readonly botRandom = (): number => {
    if (this.botSeed === 0) this.botSeed = (this.config.seed ^ 0x9e3779b9) >>> 0;
    this.botSeed = (this.botSeed + 0x6d2b79f5) >>> 0;
    let t = this.botSeed;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  private setBotThinking(value: boolean): void {
    if (this.botThinking === value) return;
    this.botThinking = value;
    this.emit();
  }

  private wait(ms: number): Promise<void> {
    if (ms <= 0) return Promise.resolve();
    return new Promise((resolve) => {
      const timer = setTimeout(() => {
        this.timers.delete(timer);
        resolve();
      }, ms);
      this.timers.add(timer);
    });
  }

  // --- log ---------------------------------------------------------------

  private appendLog(events: GameEvent[]): void {
    const names = nameMapFor(this.state);
    const added: LogLine[] = [];
    for (const event of events) {
      const text = renderEvent(event, names);
      if (text === null) continue;
      added.push({ id: this.logId++, turn: event.turn, text, refs: refsOf(event) });
    }
    if (added.length > 0) this.log = [...this.log, ...added];
  }

  private emit(): void {
    if (this.disposed) return;
    const snapshot = this.snapshot();
    for (const listener of this.listeners) listener(snapshot);
  }
}

/** Instance ids an event mentions, so the log can make names clickable. */
function refsOf(event: GameEvent): string[] {
  const out: string[] = [];
  const record = event as unknown as Record<string, unknown>;
  for (const key of ['instanceId', 'sourceInstanceId', 'targetInstanceId']) {
    const value = record[key];
    if (typeof value === 'string') out.push(value);
  }
  return out;
}
